import { useState, useCallback } from 'react';
import { AlertTriangle, CheckCircle, Bell, BellOff } from 'lucide-react';
import { FloatingPanel } from '../map/FloatingPanel';
import { AlarmCodes } from '../../store/detailedSimulation';

// Severity mapping
type Severity = 'critical' | 'warning' | 'info';
const ALARM_SEVERITY: Record<string, Severity> = {
  C: 'critical', I: 'critical', E: 'critical',
  A: 'warning', B: 'warning', J: 'warning', L: 'warning', V: 'warning', N: 'warning',
};

const SEVERITY_COLORS: Record<Severity, string> = {
  critical: 'text-red-400',
  warning: 'text-amber-400',
  info: 'text-blue-400',
};


export interface MockAlarm {
  id: number;
  driverId: number;
  code: string;
  timestamp: number;
  resolvedAt?: number;
}

let nextAlarmId = 1;

function formatTime(ts: number): string {
  const d = new Date(ts);
  return d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

export function useAlarmSimulation() {
  const [alarms, setAlarms] = useState<MockAlarm[]>([]);

  const addAlarm = useCallback((driverId: number, code: string) => {
    setAlarms(prev => {
      // Skip if same driver+code was added in the last 5 seconds (debounce)
      const now = Date.now();
      if (prev.some(a => a.driverId === driverId && a.code === code && !a.resolvedAt && now - a.timestamp < 5000)) return prev;
      const alarm: MockAlarm = { id: nextAlarmId++, driverId, code, timestamp: now };
      return [alarm, ...prev].slice(0, 20);
    });
  }, []);

  const resolveAlarm = useCallback((id: number) => {
    setAlarms(prev => prev.map(a =>
      a.id === id ? { ...a, resolvedAt: Date.now() } : a
    ));
  }, []);

  const cleanResolved = useCallback(() => {
    const now = Date.now();
    setAlarms(prev => prev.filter(a => !a.resolvedAt || now - a.resolvedAt < 30000));
  }, []);

  return { alarms, addAlarm, resolveAlarm, cleanResolved };
}

export function AlarmPanel({ alarms, highlightId }: { alarms: MockAlarm[]; highlightId?: string }) {
  const activeCount = alarms.filter(a => !a.resolvedAt).length;

  return (
    <FloatingPanel
      title={`Alarmas${activeCount > 0 ? ` (${activeCount})` : ''}`}
      position="bottom-left"
      defaultCollapsed={false}
      highlightId={highlightId}
      icon={activeCount > 0
        ? <Bell className="w-3 h-3 text-amber-400" />
        : <BellOff className="w-3 h-3 text-gray-500" />
      }
    >
      <div className="max-h-48 overflow-y-auto min-w-[240px] sm:min-w-[320px]">
        {alarms.length === 0 ? (
          <div className="px-3 py-3 text-xs text-gray-500 flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-green-500" />
            Sin alarmas activas
          </div>
        ) : (
          alarms.map(alarm => {
            const isResolved = !!alarm.resolvedAt;
            const severity = ALARM_SEVERITY[alarm.code] || 'info';
            const description = AlarmCodes[alarm.code as keyof typeof AlarmCodes] || alarm.code;
            return (
              <div
                key={alarm.id}
                className={`px-3 py-1.5 border-b border-gray-700/30 last:border-0 transition-colors ${isResolved ? 'opacity-50' : 'hover:bg-gray-800/30'}`}
              >
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-gray-500 font-mono flex-shrink-0">
                    {formatTime(alarm.timestamp)}
                  </span>
                  {isResolved
                    ? <CheckCircle className="w-3 h-3 flex-shrink-0 text-green-400" />
                    : <AlertTriangle className={`w-3 h-3 flex-shrink-0 ${SEVERITY_COLORS[severity]}`} />
                  }
                  <span className={`text-[11px] font-mono font-medium ${isResolved ? 'text-green-400/70 line-through' : 'text-white'}`}>
                    R{alarm.driverId}
                  </span>
                  <span className="text-[10px] text-gray-400 font-mono">{alarm.code}</span>
                  <span className={`text-[11px] truncate flex-1 ${isResolved ? 'text-gray-500 line-through' : 'text-gray-300'}`}>{description}</span>
                </div>
              </div>
            );
          })
        )}
      </div>
    </FloatingPanel>
  );
}

/** Returns alarm count per driver (only active alarms, for CabinetMarker badges) */
export function useAlarmCounts(alarms: MockAlarm[]): Record<number, number> {
  const counts: Record<number, number> = {};
  for (const a of alarms) {
    if (!a.resolvedAt) counts[a.driverId] = (counts[a.driverId] || 0) + 1;
  }
  return counts;
}
